<template>
  <v-form id="patient-details-grid" ref="form" v-model="valid" lazy-validation>
    <h2 class="large mb-10 mt-4">{{ title }}</h2>
    <v-alert
      v-if="error !== null"
      class="top-spaced-item multi-line custom-alert mb-6"
      type="error"
    >
      <v-icon left color="#BF0000">mdi-alert-circle</v-icon>
      {{ error }}
    </v-alert>
    <v-alert
      text
      color="#BF0000"
      type="error"
      class="top-spaced-item multi-line custom-alert pb-0 pl-14 mb-8"
      v-if="store.isPrenatal"
      :icon="false"
    >
      <b>Please note:</b>
      <p>
        Please complete this section with the mother's details and select the
        appropriate fetal sample type.
      </p>
    </v-alert>
    <v-row>
      <v-col class="custom-col col-4">
        <span class="text-title" v-if="store.isPrenatal"
          >Fetus' patient ID (if applicable)</span
        >
        <span class="text-title" v-else>Patient ID (if applicable)</span>
        <v-text-field
          v-model="externalId"
          id="patient.externalId"
          outlined
          :maxlength="limitExternalID"
          hide-details="auto"
        ></v-text-field>
        <span class="text-hint"
          >Unique patient identification number. E.g. UR or MRN</span
        >
      </v-col>
      <v-col v-if="store.isPrenatal">
        <div>
          <span class="text-title">Fetus' sample type*</span>
          <v-select
            v-model="sampleType"
            id="patient.sampleType"
            :items="probandPrenatalSampleTypeList"
            item-text="displayText"
            item-value="value"
            outlined
            :rules="ruleNotNull"
            hide-details="auto"
          ></v-select>
        </div>
      </v-col>
      <v-col v-if="!store.isPrenatal">
        <span class="text-title">Sample type*</span>
        <v-select
          v-model="sampleType"
          id="patient.sampleType"
          :items="probandSampleTypeList"
          item-text="displayText"
          item-value="value"
          style="grid-area: sampleType"
          outlined
          :rules="ruleNotNull"
          hide-details="auto"
        ></v-select>
      </v-col>
      <v-col class="custom-col">
        <div v-if="sampleType === 'STORED_DNA' || sampleType === 'STORED_DATA'">
          <span class="text-title">VCGS sample ID</span>
          <v-text-field
            v-model="storedSampleId"
            id="patient.storedSampleId"
            :rules="ruleStoredSampleId"
            style="grid-area: storedSampleId"
            outlined
            v-if="sampleType === 'STORED_DNA' || sampleType === 'STORED_DATA'"
            hide-details="auto"
          ></v-text-field>
        </div>
        <div v-if="sampleType === 'NON_VCGS_SAMPLE'">
          <span class="text-title">Non-VCGS sample ID</span>
          <v-text-field
            v-model="nonVCGSSampleID"
            id="patient.nonVCGSSampleID"
            :rules="ruleStoredSampleId"
            style="grid-area: storedSampleId"
            outlined
            v-if="sampleType === 'NON_VCGS_SAMPLE'"
            hide-details="auto"
          ></v-text-field>
        </div>
      </v-col>
    </v-row>
    <!-- Alert only applies to postnatal tests with sampleType STORED_DNA -->
    <v-alert
      text
      id="patient.sampleTypeMsg"
      type="warning"
      outlined
      v-if="!store.isPrenatal && sampleType === 'STORED_DNA'"
      icon="mdi-cash-usd-outline"
    >
      <b :icon="false">Important:</b>
      If sample is over 3 years old, please collect a fresh sample. You can
      check the age of the stored DNA from the first two digits of the sample ID
      (e.g. VCGS sample # 21WXXXXXX is from 2021). Exceptions can apply if a new
      collection is not possible/hard to obtain, please contact
      <a href="mailto:VCGSgenomics@vcgs.org.au">VCGSgenomics@vcgs.org.au</a> to
      discuss.
    </v-alert>
    <v-alert
      text
      id="patient.sampleTypeMsg"
      type="warning"
      outlined
      v-if="sampleType === 'STORED_DATA'"
      icon="mdi-cash-usd-outline"
    >
      <b :icon="false">Important:</b>
      <p :icon="false">
        This option is only available where a previous clinical exome or whole
        genome sequencing test has been performed at VCGS. Stored data can be
        used for reanalysis. Upon submission, VCGS will review this request and
        contact you, if necessary, regarding feasibility and pricing.
      </p>
    </v-alert>
    <!-- Postnatal test with NON_VCGS_SAMPLE sampleType -->
    <v-alert
      text
      id="patient.sampleTypeMsg"
      type="warning"
      color="red"
      outlined
      icon="mdi-cash-usd-outline"
      class="mr-1"
      v-if="!store.isPrenatal && sampleType === 'NON_VCGS_SAMPLE'"
    >
      <b :icon="false">Important:</b> Fresh samples are required for testing.
      Extracted DNA can be accepted when a sample is precious/hard to obtain
      such as for prenatals, POCs, or from a deceased person. For any enquiries,
      please contact
      <a href="mailto:VCGSgenomics@vcgs.org.au">VCGSgenomics@vcgs.org.au</a>.
    </v-alert>
    <!-- Prenatal test with NON_VCGS_SAMPLE sampleType -->
    <div
      style="grid-area: nonVcgsDnaCheckbox"
      v-if="store.isPrenatal && sampleType === 'NON_VCGS_SAMPLE'"
    >
      <CheckQuestionRow
        :isChecked="nonVcgsDnaAcknowledgementIsChecked"
        @updateAck="updateNonVcgsDnaAck"
        testId="patient-non-vcgs-sample"
      >
        <p>By checking this box, I acknowledge the following:</p>
        <ul>
          <li v-for="(txt, index) in nonVcgsDnaAcknowledgements" :key="index">
            {{ txt }}
          </li>
        </ul>
      </CheckQuestionRow>
      <v-row>
        <v-col class="custom-col">
          <p
            v-if="isNonVcgsCheckboxDirty && !isNonVcgsDnaCheckboxValidated"
            class="red--text"
          >
            Terms and conditions must be acknowledged
          </p>
        </v-col>
      </v-row>
    </div>
    <v-alert
      text
      id="patient.sampleTypeMsg"
      type="warning"
      outlined
      icon="mdi-cash-usd-outline"
      class="mr-1"
      v-if="
        sampleType === 'BLOOD' ||
          sampleType === 'SALIVA_NO_KIT' ||
          sampleType === 'NON_VCGS_SAMPLE' ||
          sampleType === 'AMNIO' ||
          sampleType === 'CVS'
      "
    >
      <b :icon="false">Important:</b> Upon submission of this order, a
      pre-filled pathology collection form will be generated. Please download
      prior to leaving the order summary page or closing the browser, as the
      form will be inaccessible after this time.
    </v-alert>
    <v-row>
      <v-col class="custom-col">
        <span v-if="store.isPrenatal" class="text-title"
          >Mother's given name*</span
        >
        <span v-else class="text-title">Given name*</span>

        <v-text-field
          v-model="firstName"
          id="patient.firstName"
          :rules="ruleNotNull"
          outlined
          :maxlength="limitSurname"
          hide-details="auto"
        ></v-text-field>
      </v-col>
      <v-col class="custom-col">
        <span v-if="store.isPrenatal" class="text-title"
          >Mother's surname*</span
        >
        <span v-else class="text-title">Surname*</span>
        <v-text-field
          v-model="lastName"
          id="patient.lastName"
          :rules="ruleNotNull"
          outlined
          :maxlength="limitSurname"
          hide-details="auto"
        ></v-text-field>
      </v-col>
      <v-col class="custom-col">
        <span v-if="store.isPrenatal" class="text-title">Mother's sex*</span>
        <span v-else class="text-title">Sex*</span>
        <v-select
          v-if="store.isPrenatal"
          v-model="sexPrenatal"
          id="patient.sex"
          disabled
          :items="genderList"
          item-text="displayText"
          item-value="value"
          outlined
          :rules="ruleNotNull"
          hide-details="auto"
        ></v-select>
        <v-select
          v-else
          v-model="sex"
          id="patient.sex"
          :items="genderList"
          item-text="displayText"
          item-value="value"
          outlined
          :rules="ruleNotNull"
          hide-details="auto"
        ></v-select>
      </v-col>
      <v-col v-if="store.isPrenatal">
        <span class="text-title">Current gestation*</span>
        <v-text-field
          v-model="gestationLength"
          id="patient.gestationLength"
          item-text="displayText"
          item-value="value"
          outlined
          :rules="ruleNotNull"
          hide-details="auto"
        ></v-text-field>
      </v-col>
    </v-row>

    <v-row>
      <v-col class="custom-col">
        <span class="text-title mb-2">{{
          store.isPrenatal ? "Mother's date of birth*" : "Date of birth*"
        }}</span>
        <v-text-field
          class="mt-1"
          v-model="dobdd"
          id="patient.dobdd"
          :rules="dobddRule.concat(futureDateRule)"
          outlined
          @change="calculateAge"
          label="Day"
          :maxlength="limitDay"
          hide-details="auto"
        ></v-text-field>
      </v-col>
      <v-col class="pt-9">
        <v-text-field
          class="mt-1"
          v-model="dobmm"
          id="patient.dobmm"
          :rules="dobmmRule.concat(futureDateRule)"
          outlined
          @change="calculateAge"
          label="Month"
          :maxlength="limitMonth"
          hide-details="auto"
        ></v-text-field>
      </v-col>
      <v-col class="pt-9">
        <v-text-field
          class="mt-1"
          v-model="dobyy"
          id="patient.dobyy"
          :rules="dobyyRule.concat(futureDateRule)"
          outlined
          @change="calculateAge"
          :maxlength="limitYear"
          label="Year"
          hide-details="auto"
        ></v-text-field>
      </v-col>
      <v-col class="custom-col">
        <span v-if="store.isPrenatal" class="text-title"
          >Fetus's ethnicity</span
        >
        <span v-else class="text-title">Ethnicity</span>
        <v-select
          class="mt-1"
          v-model="ethnicity"
          id="patient.ethnicity"
          :items="ethnicityList"
          item-text="displayText"
          item-value="value"
          outlined
          hide-details="auto"
        ></v-select>
      </v-col>
    </v-row>

    <AddressSection
      :reuseDataObj="reusePatientData"
      :isPrenatal="store.isPrenatal"
      ref="patient_address"
      addressRef="patient"
      class="address-class"
      idPrefix="patient."
      id="patient-address"
      filled
    ></AddressSection>
    <v-alert
      text
      color="info"
      style="grid-area: emailInfo"
      outlined
      v-if="sampleType === 'SALIVA'"
    >
      Email address / mobile of patient or parent/guardian is required in order
      for us to coordinate sending out the saliva kit, where requested.
    </v-alert>
    <v-row class="mt-2">
      <v-col class="custom-col">
        <span v-if="store.isPrenatal" class="text-title">Mother's mobile*</span>
        <span v-else class="text-title">Mobile*</span>
        <v-text-field
          v-model="mobileNum"
          id="patient.mobileNum"
          :rules="ruleValidMobile"
          style="grid-area: mobileNum"
          outlined
          :maxlength="limitMobile"
          hide-details="auto"
        ></v-text-field>
      </v-col>
      <v-col class="custom-col col-4">
        <span v-if="store.isPrenatal" class="text-title">Mother's email</span>
        <span v-else class="text-title">Email</span>
        <v-text-field
          v-model="emailAddress"
          id="patient.emailAddress"
          :rules="emailRule"
          style="grid-area: emailAddress"
          outlined
          :maxlength="limitEmail"
          hide-details="auto"
        ></v-text-field>
      </v-col>
      <v-col class="custom-col">
        <span class="text-title">Medicare card no.</span>
        <v-text-field
          v-model="medicareNum"
          id="patient.medicareNum"
          :rules="mediPersonNum !== '' ? ruleMedicareNum : ruleMedicareExome"
          outlined
          :maxlength="limitMedicareCardNum"
          hide-details="auto"
        ></v-text-field>
      </v-col>
      <v-col class="custom-col">
        <span class="text-title">Medicare card position no.</span>
        <v-text-field
          v-model="mediPersonNum"
          id="patient.mediPersonNum"
          :rules="medicareNum !== '' ? ruleMedicarePosNo : ruleMedicareExome"
          outlined
          hide-details="auto"
        ></v-text-field>
      </v-col>
    </v-row>
    <span class="text-hint mt-4">
      Medical Assignment: (Section 20A of the Health Insurance Act 1973). I
      offer to assign my right to the approved pathology practitioner who will
      render the requested pathology service/s and any eligible pathologist
      determinable service/s established as necessary by the practitioner.</span
    >
    <div>
      <!-- <h3>STATUS OF PATIENT AT DATE OF REQUEST*</h3> -->
      <h3 class="mt-8">Status of patient at date of request*</h3>
      <p class="mt-2">
        <strong
          >Please note: MBS funding is unavailable to patients seen in publicly
          (State) funded clinics.</strong
        >
      </p>
      <p class="">
        Please ensure you complete the status of your patient at consultation to
        ensure correct billing:
      </p>
      <RadioQuestion
        v-for="status in patientStatus"
        :key="status.id"
        :idId="status.id"
        :testId="status.id"
        :isChecked="status.isChecked"
        @updateAck="updatePatientStatusRespond"
        class="rara"
      >
        {{ status.option }}
      </RadioQuestion>
      <p
        v-if="patientStatusChecked && !isCheckedBoxValidate"
        style="color: #ff0000"
      >
        Need to select an option *
      </p>
    </div>
  </v-form>
</template>

<script>
import FormRulesMixin from "../mixins/FormRulesMixin";
import DropDownMixin from "../mixins/DropDownMixin";
import { EventBus } from "../event-bus.js";
import RadioQuestion from "./common/RadioQuestion";
import { useSectionStore } from "../store/SectionStore";
import AddressSection from "./AddressSection.vue";
import CheckQuestionRow from "@/components/common/CheckQuestionRow";

export default {
  name: "",
  mixins: [FormRulesMixin, DropDownMixin],
  props: {
    reusePatientData: Object,
    title: String,
    patientdata: Object,
    populatekeys: Array,
    error: String,
    forceFormDirty: {
      default: false,
      type: Boolean,
    },
  },
  components: { RadioQuestion, AddressSection, CheckQuestionRow },

  mounted() {
    if (window.location.search) {
      let urlSearchParams = new URLSearchParams(window.location.search);

      // for non-production there are special keys that help us prefill the form (NOTE: this is not the technique
      // that should be used for actual automated testing i.e. cypress - this is to help out with ad-hoc manual
      // testing)
      urlSearchParams.forEach((value, key) => {
        if (key === "testPatient") {
          if (value === "maggie" || value === "") {
            this["firstName"] = "Maggie";
            this["lastName"] = "Simpson";
            this["streetAddress"] = "742 Evergreen Tce";
            this["city"] = "Springfield";
            this["sex"] = "F";
            this["state"] = "QLD";
            this["postcode"] = "3001";
            this["mobileNum"] = "0400 000 001";
            this["medicareNum"] = "1234567890";
            this["mediPersonNum"] = "1";
            this["externalId"] = "MH123456";
            this["sampleType"] = "BLOOD";
            this["sampleType"] = "AMNIO";
            this["dobdd"] = "3";
            this["dobmm"] = "11";
            this["dobyy"] = "2015";
            this["ethnicity"] = "EUROPEAN";
            this.updatePatientStatusRespond(true, "private_private_suite");
          }
        }
      });
    }

    // Load the data from the patientdata given via a prop
    // Only load the keys given
    if (this.patientdata && this.populatekeys && this.populatekeys.length > 0) {
      for (let k of this.populatekeys) {
        if (k in this.patientdata) {
          this[k] = this.patientdata[k];
        }
      }
    }
    //reset sample type to empty if test type (prenatal/postnatal) is changed
    EventBus.$on("resetSampleType", () => {
      this["sampleType"] = "";
    });
    EventBus.$on(
      [
        "medicareExomeStatus",
        "medicareChildhoodSyndromesStatus",
        "medicareAlportStatus",
        "medicareRenalNonCysticStatus",
        "medicareRenalCysticStatus",
        "medicareSCA15Status",
        "medicareMitoDiseaseStatus",
        "medicareVariantDetectionStatus",
      ],
      (status) => {
        if (status) {
          this.ruleMedicareExome = this.ruleNotNull;
        } else {
          this.ruleMedicareExome = [];
        }
      }
    );
    EventBus.$emit("getPatientData");
  },
  watch: {
    sampleType: function() {
      EventBus.$emit("getPatientData");
    },
  },
  computed: {
    nonVcgsDnaAcknowledgements() {
      const store = useSectionStore();
      let acknowledgement = this.prenatalNonVcgsDnaAcknowledgements;
      if (store.isPrenatal) {
        return acknowledgement;
      } else {
        return [...this.postnatalNonVcgsDnaAcknowledgments, ...acknowledgement];
      }
    },
    futureDateRule() {
      if (this.getCurrentDate() < this.getUserDOB()) {
        return ["Cannot pick date in the future"];
      }
      return [];
    },
    dobddRule() {
      if (this.dobdd !== "") {
        if (
          [2, 4, 6, 9, 11].includes(parseInt(this.dobmm)) &&
          parseInt(this.dobdd) === 31
        ) {
          return ["Cannot use 31 with this month"];
        }

        return this.ruleDOBday;
      } else {
        return this.ruleNotNull;
      }
    },
    dobmmRule() {
      return this.dobmm !== "" ? this.ruleDOBmonth : this.ruleNotNull;
    },
    dobyyRule() {
      return this.dobyy !== "" ? this.ruleDOByear : this.ruleNotNull;
    },
    emailRule() {
      if (this.emailAddress === "") {
        return [];
      } else {
        return this.ruleValidEmail;
      }
    },
    isCheckedBoxValidate() {
      return (
        this.patientStatus.filter((patientStat) => patientStat.isChecked)
          .length === 1
      );
    },
    isNonVcgsCheckboxDirty() {
      return this.isNonVcgsDnaCheckboxValidated != null || this.forceFormDirty;
    },
    isNonVcgsDnaCheckboxValidated() {
      return this.nonVcgsDnaAcknowledgementIsChecked === true;
    },
  },
  data: function() {
    var newData = {
      externalId: "",
      lastName: "",
      firstName: "",
      dobyy: "",
      dobmm: "",
      dobdd: "",
      ethnicity: this.reusePatientData.ethnicity || "",
      sampleType: "",
      gestationLength: "",
      storedSampleId: "",
      nonVCGSSampleID: "",
      isPrenatal: null,
      sex: "",
      sexPrenatal: "F",
      mobileNum: "",
      emailAddress: "",
      unitFlat: this.reusePatientData.unitFlat || "",
      streetAddress: this.reusePatientData.streetAddress || "",
      city: this.reusePatientData.city || "",
      state: this.reusePatientData.state || "",
      postcode: this.reusePatientData.postcode || "",
      country: this.reusePatientData.country || "AUS",
      medicareNum: this.reusePatientData.medicareNum || "",
      mediPersonNum: "",
      clinicalNotes: "",
      ruleMedicareExome: [],
      age: null,
      patientStatus: [
        {
          id: "private_private_suite",
          option:
            "Private patient in a private suite, private hospital or approved day hospital facility",
          isChecked: false,
        },
        {
          id: "private_recognised_hospital",
          option: "Private patient in a recognised hospital",
          isChecked: false,
        },
        {
          id: "public_public_hospital",
          option: "Public patient in a recognised public hospital",
          isChecked: false,
        },
        {
          id: "public_outpatient_public_hospital",
          option: "Public outpatient of a recognised public hospital",
          isChecked: false,
        },
      ],
      patientStatusAck: null,
      patientStatusContent: "",
      patientStatusChecked: false,
      nonVcgsDnaAcknowledgementIsChecked: null, // Only relevant to prenatal tests with NON_VCGS_SAMPLE sampleType
      postnatalNonVcgsDnaAcknowledgments: [
        "DNA must be extracted from EDTA blood",
      ],
      prenatalNonVcgsDnaAcknowledgements: [
        "The referring clinical service is responsible for organising the transport of the sample to VCGS",
        "DNA must be extracted in a laboratory with clinical NATA accreditation",
        "The referring laboratory is responsible for ensuring suitable quality of extracted DNA",
        "For any enquiries regarding sample suitability, please contact VCGS",
      ],
      pregnant: null,
    };
    return newData;
  },
  setup() {
    const store = useSectionStore();
    return {
      store,
    };
  },
  methods: {
    calculateAge() {
      if (!isNaN(this.dobdd) && !isNaN(this.dobmm) && !isNaN(this.dobyy)) {
        let dateString = this.dob();
        if (dateString !== "") {
          let today = new Date();
          let birthDate = new Date(dateString);
          if (!isNaN(birthDate)) {
            let age = today.getFullYear() - birthDate.getFullYear();
            let m = today.getMonth() - birthDate.getMonth();
            if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
              age--;
            }
            this.age = age;
          } else {
            this.age = null;
          }
        } else {
          this.age = null;
        }
      } else {
        this.age = null;
      }
      EventBus.$emit("getPatientData");
    },
    getCurrentDate() {
      return new Date();
    },
    getUserDOB() {
      return new Date(
        parseInt(this.dobyy),
        parseInt(this.dobmm) - 1,
        parseInt(this.dobdd)
      );
    },
    dob() {
      let val;
      if (!this.dobdd || !this.dobmm || !this.dobyy) {
        val = "";
      } else {
        val =
          this.dobyy +
          "-" +
          this.zfill(2, this.dobmm) +
          "-" +
          this.zfill(2, this.dobdd);
      }
      return val;
    },
    updateNonVcgsDnaAck(ack) {
      this.nonVcgsDnaAcknowledgementIsChecked = ack;
    },
    updatePatientStatusRespond(ack, id) {
      this.patientStatusChecked = true;
      this.patientStatusAck = false;
      if (ack === true) {
        this.patientStatusAck = true;
        this.patientStatus.forEach((patientStat) => {
          patientStat.isChecked = false;
        });
        this.patientStatus.find(
          (patientStat) => patientStat.id === id
        ).isChecked = true;
        this.patientStatusContent = id;
      } else {
        this.patientStatus.find(
          (patientStat) => patientStat.id === id
        ).isChecked = false;
      }
      EventBus.$emit("getPatientData");
    },

    isFormValid: function() {
      this.patientStatusChecked = true;
      var isValid = this.isCheckedBoxValidate;

      // Postnatal tests with sampleType NON_VCGS_SAMPLE
      if (!this.store.isPrenatal && this.sampleType === "NON_VCGS_SAMPLE") {
        isValid = isValid;
      }
      // Prenatal tests with sampleType NON_VCGS_SAMPLE
      if (this.store.isPrenatal && this.sampleType === "NON_VCGS_SAMPLE") {
        isValid = isValid && this.isNonVcgsDnaCheckboxValidated;
      }
      if (!this.$refs.patient_address.validateForm()) {
        return false;
      }
      return isValid;
    },
    transferData: function() {
      const addressData = this.$refs.patient_address.transferData();

      const store = useSectionStore();
      if (store.isPrenatal) {
        //for prenatal tests we want to store some details to prefill mothers details if it is a trio order
        store.patientDetails = {
          lastName: this.lastName,
          firstName: this.firstName,
          dobdd: this.dobdd,
          dobmm: this.dobmm,
          dobyy: this.dobyy,
          ethnicity: this.ethnicity,
          mobileNum: this.cleanPhoneNumber(this.mobileNum),
          unitFlat: addressData.unitFlat,
          streetAddress: addressData.streetAddress,
          city: addressData.city,
          state: addressData.state,
          postcode: addressData.postcode,
          country: addressData.country,
        };
        // Use the default sex
        this.sex = this.sexPrenatal;
      }

      return {
        externalId: this.externalId,
        lastName: this.lastName,
        firstName: this.firstName,
        birthDate: this.dob(),
        ethnicity: this.ethnicity,
        sampleType: this.sampleType,
        gestationLength: this.gestationLength,
        // Only relavant to prenatal tests with NON_VCGS_SAMPLE sampleType
        nonVcgsDnaStatusAck:
          this.sampleType === "NON_VCGS_SAMPLE"
            ? this.nonVcgsDnaAcknowledgementIsChecked
            : null,
        storedSampleId: this.storedSampleId,
        nonVCGSSampleID: this.nonVCGSSampleID,
        sex: this.sex,
        mobileNum: this.cleanPhoneNumber(this.mobileNum),
        emailAddress: this.emailAddress,
        unitFlat: addressData.unitFlat,
        streetAddress: addressData.streetAddress,
        city: addressData.city,
        state: addressData.state,
        postcode: addressData.postcode,
        country: addressData.country,
        medicareNum: this.medicareNum.replace(/\s/g, ""),
        mediPersonNum: this.mediPersonNum,
        age: this.age,
        patientStatusAck: this.patientStatusAck,
        patientStatusContent: this.patientStatusContent,
        pregnant: this.store.patientPregnant,
      };
    },
  },
};
</script>

<style scoped>
.rara {
  font-family: "Roboto", sans-serif;
  color: #737373;
  padding: 5px !important;
}
</style>
